// Grade → the school courses that grade studies. This is what the
// puzzle panel's subject picker is built from once a grade is chosen
// (ui/puzzlePanel.js's renderCourseButtons), replacing the old fixed
// three-button set.
//
// Modelled on the English / British National Curriculum. The app's
// "grade" number IS the UK school Year, so the key stages fall out as:
//   - KS1 → Years 1–2
//   - KS2 → Years 3–6
//   - KS3 → Years 7–9
//   - KS4 → Years 10–11
//   - Year 12+ (sixth form) sits outside the key-stage system → null.
// KS2/KS3/KS4 follow one common six-subject line in this game, so they
// share a course list; KS1 has its own (the statutory core plus the
// science-adjacent topics the curriculum breaks out as their own KS1
// units). Grade is also passed straight through to the AI generator and
// to the static fallback bank, which picks a gentler "ks1" riddle set
// for Years 1–2 (see systems/riddleOrchestrator.js and
// data/riddles/index.js's riddleBandForGrade).
//
// Only the grades in ENABLED_GRADES are selectable in the grade pickers;
// everything else is shown disabled ("coming soon"). See data/grades.js
// and docs/decisionlog.md.

const COURSE = {
  math:      { id: "math",      label: "Maths",     icon: "➗" },
  english:   { id: "english",   label: "English",   icon: "📖" },
  science:   { id: "science",   label: "Science",   icon: "🔬" },
  history:   { id: "history",   label: "History",   icon: "🏰" },
  geography: { id: "geography", label: "Geography", icon: "🗺️" },
  computing: { id: "computing", label: "Computing", icon: "💻" },
  animals:   { id: "animals",   label: "Animals",   icon: "🐾" },
  plants:    { id: "plants",    label: "Plants",    icon: "🌱" },
  "everyday-materials": { id: "everyday-materials", label: "Everyday Materials", icon: "🧱" }
};

// Max six courses per key stage, by product decision — keeps the picker
// to two tidy rows.

// KS1 (Years 1–2). English, maths and science are statutory; "animals",
// "plants" and "everyday-materials" mirror the named KS1 science units
// and each gets its own gentle riddle bank (data/riddles/).
const KS1_COURSES = [
  COURSE.english,
  COURSE.math,
  COURSE.science,
  COURSE.animals,
  COURSE.plants,
  COURSE["everyday-materials"]
];

// KS2 (Years 3–6), KS3 (Years 7–9) and KS4 (Years 10–11) share one
// course line here. `math` and `geography` reuse the original riddle
// banks; `english`, `science`, `history` and `computing` got their own
// compact banks in the same pass. The fallback bank serves all three
// key stages from one shared "ks2plus" set (difficulty tiers carry the
// age spread) — see data/riddles/index.js.
const KS2_PLUS_COURSES = [
  COURSE.english,
  COURSE.math,
  COURSE.science,
  COURSE.geography,
  COURSE.history,
  COURSE.computing
];

const COURSES_BY_KEY_STAGE = {
  ks1: KS1_COURSES,
  ks2: KS2_PLUS_COURSES,
  ks3: KS2_PLUS_COURSES,
  ks4: KS2_PLUS_COURSES
};

/** The National Curriculum key stage a school Year ("grade") sits in,
 * or null for Year 12+ (sixth form — outside the key-stage system). */
export function keyStageForGrade(grade) {
  const g = Number(grade);
  if (g >= 1 && g <= 2) return "ks1";
  if (g >= 3 && g <= 6) return "ks2";
  if (g >= 7 && g <= 9) return "ks3";
  if (g >= 10 && g <= 11) return "ks4";
  return null;
}

/** School Years the grade pickers actually offer. Deliberately a small
 * explicit allow-list — the course + fallback model already covers every
 * Year 1–11, but a Year is only switched on once its content has been
 * reviewed. Sorted ascending, so ENABLED_GRADES[0] is the youngest (and
 * data/grades.js's DEFAULT_GRADE). */
export const ENABLED_GRADES = [1, 6, 8];

/** The course list for a grade: `[]` for a Year the pickers don't offer
 * yet, or one with no key stage (12+). A stale save pointing at a
 * now-disabled grade hits the `[]` path and main.js sends the player
 * back to the picker. */
export function coursesForGrade(grade) {
  if (!ENABLED_GRADES.includes(Number(grade))) return [];
  const ks = keyStageForGrade(grade);
  return ks ? COURSES_BY_KEY_STAGE[ks] : [];
}

/** Back-compat view: the enabled grades mapped to their course lists.
 * Prefer coursesForGrade() / keyStageForGrade() for new code. */
export const COURSES_BY_GRADE = Object.fromEntries(
  ENABLED_GRADES.map((grade) => [grade, coursesForGrade(grade)])
);

/** Whether a grade can be picked at all. */
export function isGradeEnabled(grade) {
  return ENABLED_GRADES.includes(Number(grade));
}

/** Every distinct course id across all key stages — lets the Worker
 * validate a `subject` query param without re-listing them, and stays
 * complete even while ENABLED_GRADES is a subset of the Years. */
export const ALL_COURSE_IDS = [
  ...new Set(Object.values(COURSES_BY_KEY_STAGE).flat().map((course) => course.id))
];

/** Player-facing label for a course id ("english" → "English"), for
 * activity-log lines and the like. Falls back to a capitalised id for
 * an id with no course entry (e.g. a legacy `food`). */
export function courseLabelFor(id) {
  return COURSE[id]?.label ?? (id ? id.charAt(0).toUpperCase() + id.slice(1) : id);
}

/** The course's emoji ("english" → "📖"), for stat readouts and the like.
 * Falls back to a neutral book glyph for an id with no course entry. */
export function courseIconFor(id) {
  return COURSE[id]?.icon ?? "📚";
}
